import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useUpdatePublicProfile } from '../../hooks/queries/useLawyerProfile';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { cn } from '../../lib/cn';

export function ProfilePublicSection() {
  const collaborator = useAuthStore((s) => s.collaborator);
  const setCollaborator = useAuthStore((s) => s.setCollaborator);
  const mutation = useUpdatePublicProfile();

  const [bio, setBio] = useState('');
  const [expertise, setExpertise] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showDossiers, setShowDossiers] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!collaborator) return;
    setBio(collaborator.bio ?? '');
    setExpertise(collaborator.expertise ?? []);
    setShowDossiers(collaborator.show_dossiers ?? false);
  }, [collaborator]);

  if (!collaborator) return null;

  function addTag() {
    const t = tagInput.trim();
    if (t && !expertise.includes(t)) setExpertise((prev) => [...prev, t]);
    setTagInput('');
  }

  function save() {
    const update = { bio: bio.trim() || null, expertise, show_dossiers: showDossiers };
    mutation.mutate(
      { id: collaborator!.id, update },
      {
        onSuccess: () => {
          setCollaborator({ ...collaborator, ...update } as typeof collaborator);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }

  return (
    <div className="bg-cime-surface rounded-cime border border-neutral-200 p-6 space-y-5">
      <h2 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">Profil public</h2>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-neutral-900">Biographie</label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={4}
          placeholder="Parcours, spécialités, langues parlées…"
          className="w-full rounded-cime border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cime-primary"
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-neutral-900">Domaines de compétence</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {expertise.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-cime-primary/10 text-cime-primary"
            >
              {tag}
              <button
                type="button"
                onClick={() => setExpertise((p) => p.filter((x) => x !== tag))}
                aria-label={`Retirer ${tag}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            placeholder="Droit pénal, droit de la famille…"
          />
          <Button variant="secondary" onClick={addTag} type="button">
            Ajouter
          </Button>
        </div>
      </div>

      <div className="flex items-start justify-between gap-6 pt-2">
        <div className="space-y-1">
          <p className="text-sm font-medium text-neutral-900">Rendre mes dossiers en cours visibles</p>
          <p className="text-sm text-neutral-500 max-w-md">
            Vos collaborateurs pourront voir la liste de vos dossiers actifs et votre charge de
            travail sur votre profil. L'administrateur y a toujours accès.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={showDossiers}
          onClick={() => setShowDossiers((v) => !v)}
          className={cn(
            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
            showDossiers ? 'bg-cime-primary' : 'bg-neutral-300',
          )}
        >
          <span
            className={cn(
              'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition',
              showDossiers ? 'translate-x-5' : 'translate-x-0',
            )}
          />
        </button>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button variant="primary" onClick={save} disabled={mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
        {saved && <span className="text-xs text-green-600 font-medium">Profil mis à jour.</span>}
        {mutation.isError && <span className="text-xs text-red-600">Erreur. Réessayez.</span>}
      </div>
    </div>
  );
}
